import * as React from 'react';
import { HocOptions } from '../common/generic-hoc.types';
import { StylesProps, ThemeType } from '../../theme/jss-types';
import Paginator from '../common/paginator';
import moment from 'moment';
import { NextOrPrev, Pagination } from '../../library/Types';
import { Statement, WalletPages } from './wallet.types';
import { RouteChildrenProps } from 'react-router';
import { Routes } from '../../library/constants';
import GenericHoc from '../common/generic-hoc';
import { walletColumns } from './wallet.constants';
import { walletStyles } from './wallet.styles';
import {
    fetchCustomerWalletData,
    fetchWalletLedger,
    fetchWalletBlockedAmount,
    fetchRechargeTransactions,
    getRedirectionURL,
    fetchPaymentStatus,
} from '../../network/wallet.api';
import WalletIcon from '../../assets/wallet';
import {
    Button, DatePicker, Drawer, Input, message, Spin, Tooltip,
} from 'antd';
import WalletFaq from './wallet-faq';
import { ReduxStore } from 'reducers/redux.types';
import {
    Master,
} from '../../types/master-data-types';
import { ReloadOutlined } from '@ant-design/icons';
import { get } from 'lodash';
import reloadSvg from './icons/reload.svg';
import { NAVBAR_HEIGHT } from 'library/globals';
import { storeCNCDataState } from 'actions/miscellaneous-actions';
import { bindActionCreators } from 'redux';
import { useTranslation } from 'react-i18next';
import FixedHeaderTable from 'components/common/FixedHeaderTable/FixedHeaderTable';

interface WalletProps
    extends StylesProps<ReturnType<typeof walletStyles>>, RouteChildrenProps {
    uiTheme: ThemeType;
    master: Master;
    setCNCData: (state: Record<any, any>) => void;
}

const {
    useEffect,
} = React;

const AUTO_CHECK_FIRST_RECHARGE_ROW_KEY = 'wallet_auto_check_first_recharge_row';

const WalletPage = (props: WalletProps) => {
    const {
        classes,
        history,
        master,
        uiTheme,
        setCNCData,
    } = props;

    const { t } = useTranslation();

    const [rechargeVisible, setRechargeVisible] = React.useState(false);
    const [faqVisible, setFaqVisible] = React.useState(false);
    const customerProperties = master?.properties;
    const enable_customer_config = master?.parts_to_show?.enable_customer_config || false;
    const paymentModesList = master?.customer_wallet_config?.customerWalletPaymentMethods || [];
    const precisionValue = master?.config?.precision_value || 3;
    const timezone = master?.config?.timezone;
    const bannerHeight = (master?.config?.customer_portal_config?.password_policy_banner_expiry_date
        && new Date(master?.config?.customer_portal_config?.password_policy_banner_expiry_date) <= new Date()) ? 0 : 50;
    let reference_id:string;
    if (Array.isArray(paymentModesList) && paymentModesList.length > 0) {
        reference_id = paymentModesList[0]?.reference_id;
    }

    let showWallet = true;
    if (customerProperties && enable_customer_config) {
        showWallet = customerProperties?.Wallet;
    }

    const pageSelected = React.useMemo((): WalletPages => {
        if (!showWallet) {
            return 'WALLET_LEDGER';
        }
        switch (history.location.pathname) {
            case `/${Routes.RECHARGE_TRANSACTIONS}`: return 'RECHARGE_TRANSACTIONS';
            case `/${Routes.BLOCKED_AMOUNT_LEDGER}`: return 'BLOCKED_AMOUNT';
            case `/${Routes.WALLET_LEDGER}`: return 'WALLET_LEDGER';
            default: return 'WALLET_LEDGER';
        }
    }, []);

    const [toggle, setToggle] = React.useState<boolean>(true);
    const [statement, setStatement] = React.useState<Statement[]>([]);
    const [walletLedgerData, setWalletLedgerData] = React.useState<any>({});
    const [blockedLedger, setblockedLedger] = React.useState<Statement[]>([]);
    const [rechargeTransactions, setRechargeTransactions] = React.useState<Statement[]>([]);
    const [customerWalletData, setCustomerWalletData] = React.useState<any>({});
    const [walletLedgerSection, setWalletLedgerSection] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [amount, setAmount] = React.useState(0);
    const [isReloadDisabled, setIsReloadDisabled] = React.useState(false);
    const [filters, setFilters] = React.useState({
        fromDate: moment().subtract(7, 'days'),
        toDate: moment(),
    });
    const [pagination, setPagination] = React.useState<Pagination>({
        currentPageNumber: 1,
        resultPerPage: 50,
        nextOrPrev: 'first',
        isNextPresent: false,
    });

    const getParams = () => {
        return {
            fromDate: moment(filters.fromDate).format('YYYY-MM-DD'),
            toDate: moment(filters.toDate).format('YYYY-MM-DD'),
            limit: pagination.resultPerPage,
            pageNumber: pagination.currentPageNumber - 1,
            timezone,
        };
    };

    const loadWalletLedger = async () => {
        setLoading(true);
        const params = getParams();
        const response = await fetchWalletLedger(
            params,
        );
        setStatement(response?.data?.transactionLogs || []);
        const isNext = response?.data?.isNextPresent || false;
        const newPagination = {
            ...pagination,
            isNextPresent: isNext,
        };
        setPagination(newPagination);
        setWalletLedgerData(response?.data || {});
        if (!response?.isSuccess) {
            message.error(response.errorMessage);
        }
        setLoading(false);
    };
    const loadBlockedAmount = async () => {
        const params = getParams();
        setLoading(true);
        const response = await fetchWalletBlockedAmount(
            params,
        );

        setblockedLedger(response?.data?.transactionLogs || []);
        const isNext = response?.data?.isNextPresent || false;
        const newPagination = {
            ...pagination,
            isNextPresent: isNext,
        };
        setPagination(newPagination);
        if (!response?.isSuccess) {
            message.error(response.errorMessage);
        }
        setLoading(false);
    };

    const loadRechargeTransactions = async () => {
        const params = getParams();
        setLoading(true);
        const response = await fetchRechargeTransactions(
            params,
        );

        setRechargeTransactions(response?.data?.transactionLogs || []);
        const isNext = response?.data?.isNextPresent || false;
        const newPagination = {
            ...pagination,
            isNextPresent: isNext,
        };
        setPagination(newPagination);
        if (!response?.isSuccess) {
            message.error(response.errorMessage);
        }
        setLoading(false);
    };

    const loadStatement = () => {
        if (pageSelected === 'WALLET_LEDGER') {
            setWalletLedgerSection(true);
            loadWalletLedger();
        } else if (pageSelected === 'BLOCKED_AMOUNT') {
            setWalletLedgerSection(false);
            loadBlockedAmount();
        } else {
            setWalletLedgerSection(false);
            loadRechargeTransactions();
        }
    };

    const loadCustomerWalletData = async () => {
        const response = await fetchCustomerWalletData();
        if (!response?.isSuccess) {
            message.error(response.errorMessage);
        } else {
            setCustomerWalletData(response?.data || {});
            setCNCData(response?.data || {});
        }
    };

    useEffect(() => {
        loadCustomerWalletData();
        loadStatement();
    }, [pageSelected, filters, pagination.currentPageNumber]);

    const renderText = (text: string) => {
        return (
            <div
                className={classes.cellValue}
            >
                {text}
            </div>
        );
    };

    const reloadPage = () => {
        if (!isReloadDisabled) {
            setIsReloadDisabled(true);
            loadStatement();
            setTimeout(() => {
                setIsReloadDisabled(false);
            }, 10000);
        }
    };

    const checkPaymentStatus = async (record: any) => {
        if (record?.status === 'PENDING') {
            setLoading(true);
            const response = await fetchPaymentStatus({ transactionId: record?.transaction_id, referenceId: reference_id });
            if (response?.isSuccess) {
                const currentPaymentStatus = get(response, 'data.paymentStatusResult.data.payment_status');
                if (currentPaymentStatus === 'SUCCESS') {
                    message.success(`${t('Payment status')}: ${t(currentPaymentStatus)}`);
                } else {
                    message.warning(`${t('Payment status')}: ${t(currentPaymentStatus)}`);
                }
                reloadPage();
                await loadCustomerWalletData();
            } else {
                message.error(t('failed_to_fetch_payment_status'));
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        if (pageSelected !== 'RECHARGE_TRANSACTIONS') return;
        const shouldAutoCheckFirstRow = localStorage.getItem(AUTO_CHECK_FIRST_RECHARGE_ROW_KEY) === '1';
        if (!shouldAutoCheckFirstRow || rechargeTransactions.length === 0) return;

        localStorage.removeItem(AUTO_CHECK_FIRST_RECHARGE_ROW_KEY);
        checkPaymentStatus(rechargeTransactions[0]);
    }, [pageSelected, rechargeTransactions]);

    const renderCheckPaymentStatus = (status: string) => {
        return (
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span className={classes.cellValue}>
                        {status}
                    </span>
                    <div className={classes.reloadSvgButton}>
                        <img src={reloadSvg} alt="Not Found" style={{ height: '18px', width: '18px' }} />
                    </div>
                </div>
            </div>
        );
    };

    const renderColumn = (text: string, column: any) => {
        if (!text || text === null) {
            return <div className={classes.cellNa}>-</div>;
        }

        switch (column) {
            case 'date':
            case 'created_at': {
                const dateString = moment(text).format('DD MMM, YYYY | HH:mm:ss');
                return renderText(dateString);
            }
            case 'credit_debit_flag':
                return renderText(text?.toUpperCase());
            case 'status':
                return text === 'SUCCESS' ? renderText(text) : renderCheckPaymentStatus(text);
            case 'amount':
            case 'wallet_balance': {
                return renderText(Intl.NumberFormat('en-IN', {
                    maximumFractionDigits: precisionValue,
                }).format(Number(text)));
            }
            default:
                return renderText(text);
        }
    };

    const getWidth = (id: string) => {
        switch (id) {
            case 'action_type':
            case 'actions': return 60;
            case 'status':
            case 'reference_number':
            case 'destination_name':
            case 'creation_source':
            case 'courier_partner_reference_number':
            case 'customer_reference_number': return 150;
            case 'movement_type':
            default: return 120;
        }
    };

    const getColumns = (): any[] => {
        const columnLables: Record<string, string> = walletColumns[pageSelected];

        const columns: any = Object.keys(columnLables).map((column: string) => {
            const columnConfig = {
                key: column,
                title: t(columnLables[column]),
                dataIndex: column,
                width: getWidth(column),
                ellipsis: true,
                fixed: column === 'actions' ? 'right' : undefined,
                render: (text: string) => renderColumn(text, column),
                ...(column === 'status' && {
                    onCell: (record: any) => ({
                        onClick: () => checkPaymentStatus(record),
                    }),
                }),
            };

            return columnConfig;
        });
        return columns;
    };

    const getDataSource = () => {
        switch (pageSelected) {
            case 'WALLET_LEDGER': return statement;
            case 'RECHARGE_TRANSACTIONS': return rechargeTransactions;
            case 'BLOCKED_AMOUNT':
            default: return blockedLedger;
        }
    };

    const renderTable = () => {
        return (
            <FixedHeaderTable
                bordered={false}
                pagination={false}
                loading={loading}
                rowKey={(row: Statement) => row.id}
                columns={getColumns()}
                scroll={{
                    y: `calc(
                        (((100vh - ${NAVBAR_HEIGHT}px) - 55px) - ${bannerHeight}px) 
                        - ${130}px)`, // Sum of Balance bar height, page filter height and extra filters height
                }}
                style={{
                    height: `calc(
                        (((100vh - ${NAVBAR_HEIGHT}px) - 130px) - ${bannerHeight}px) )`,
                    background: '#ffffff',
                }}
                dataSource={getDataSource() || []}
            />
        );
    };

    const renderRangePicker = () => {
        return (
            <div className={classes.filter}>
                <DatePicker.RangePicker
                    value={[moment(filters.fromDate), moment(filters.toDate)]}
                    dropdownClassName={classes.dateRange}
                    placeholder={[t('select_start_date'), t('select_end_date')]}
                    onChange={(value) => {
                        if (!(value && value[0] && value[1])) return;
                        const newFilters = {
                            ...filters,
                        };
                        newFilters.fromDate = moment(value[0]);
                        newFilters.toDate = moment(value[1]);
                        setFilters(newFilters);
                    }}
                />
            </div>
        );
    };

    const renderDateFilter = () => {
        return (
            <div className={classes.dateFilter}>
                {renderRangePicker()}
            </div>
        );
    };

    const renderOpeningClosingBalance = () => {
        if (!walletLedgerData || Object.keys(walletLedgerData).length === 0) return null;

        return (
            <div className={classes.openingClosingBalanceSecton}>
                <div className={classes.balanceSection}>
                    <div style={{ fontWeight: 'bold' }}>
                        {Intl.NumberFormat('en-IN', {
                            maximumFractionDigits: precisionValue,
                        }).format(walletLedgerData?.openingBalance)}
                    </div>
                    <div style={{ fontSize: '11px' }}>
                        {t('opening_balance')}
                    </div>
                </div>
                <div className={classes.balanceSection}>
                    <div style={{ fontWeight: 'bold' }}>
                        {Intl.NumberFormat('en-IN', {
                            maximumFractionDigits: precisionValue,
                        }).format(walletLedgerData?.closingBalance)}
                    </div>
                    <div style={{ fontSize: '11px' }}>
                        {t('closing_balance')}
                    </div>
                </div>
            </div>
        );
    };

    const renderTitle = () => {
        const titleText = !isReloadDisabled ? t('reload') : t('reload_available_soon');

        return (
            <span
                style={{
                    display: 'inline-block',
                    maxWidth: '100px',
                    textAlign: 'center',
                    textJustify: 'inter-word',
                    wordBreak: 'break-word',
                    lineHeight: '1.2',
                }}
            >
                {titleText}
            </span>
        );
    };

    const renderRefresh = () => {
        return (
            <Tooltip title={renderTitle}>
                <Button
                    size="small"
                    type="ghost"
                    style={{
                        color: uiTheme.primaryColor,
                        fontSize: 14,
                    }}
                    icon={<ReloadOutlined />}
                    disabled={isReloadDisabled}
                    onClick={() => reloadPage()}
                />
            </Tooltip>
        );
    };

    const renderLeftFilters = () => {
        return (
            <div className={classes.leftFilters}>
                {renderRefresh()}
                {walletLedgerSection && renderOpeningClosingBalance()}
            </div>
        );
    };

    const handlePagination = (nextOrPrev: NextOrPrev) => {
        const { currentPageNumber } = pagination || {};
        let newPageNumber = currentPageNumber;
        if (nextOrPrev === 'first') {
            newPageNumber = 1;
        } else if (nextOrPrev === 'next') {
            newPageNumber = currentPageNumber + 1;
        } else {
            newPageNumber = currentPageNumber - 1;
        }
        const newPagination = {
            ...pagination,
            nextOrPrev,
            currentPageNumber: newPageNumber,
        };
        setPagination(newPagination);
        setToggle(!toggle);
    };

    const renderPaginator = () => {
        return (
            <Paginator
                currentPageNumber={pagination?.currentPageNumber || 1}
                isNextPresent={pagination?.isNextPresent}
                onNextClick={() => handlePagination('next')}
                onPrevClick={() => handlePagination('prev')}
                onFirstClick={() => handlePagination('first')}
            />
        );
    };

    const renderRightFilters = () => {
        return (
            <div className={classes.rightFilters}>
                {renderDateFilter()}
            </div>
        );
    };

    const renderExtraFilters = () => {
        return (
            <div className={classes.extraFilters}>
                {renderLeftFilters()}
                {renderRightFilters()}
            </div>
        );
    };

    const renderFilters = () => {
        return (
            <div className={classes.filters}>
                {renderExtraFilters()}
            </div>
        );
    };

    const renderIcon = () => {
        return (
            <WalletIcon
                style={{
                    fontSize: 40,
                    color: '#FFFFFF',
                }}
            />
        );
    };

    const renderDrawerIcon = () => {
        return (
            <WalletIcon
                style={{
                    fontSize: 40,
                    color: uiTheme.primaryColor,
                }}
            />
        );
    };

    const renderCurrentBalance = () => {
        const availableBalance = Number(customerWalletData?.availableBalance || 0);
        const blockedBalance = Number(customerWalletData?.blockedBalance || 0);
        return (
            <div className={classes.balance}>
                <div>
                    {Intl.NumberFormat('en-IN', {
                        maximumFractionDigits: precisionValue,
                    }).format(availableBalance + blockedBalance)}
                </div>
                <span className={classes.balanceText}>
                    {t('total_balance')}
                </span>
            </div>
        );
    };

    const renderSign = (sign: any) => {
        return (
            <span className={classes.signText}>
                {sign}
            </span>
        );
    };

    const renderMinumumBalance = () => {
        const minimumBalance = Number(customerWalletData?.minimumBalance || 0);
        return (
            <div className={classes.balance}>
                <div>
                    {Intl.NumberFormat('en-IN', {
                        maximumFractionDigits: precisionValue,
                    }).format(minimumBalance)}
                </div>
                <span className={classes.balanceText}>
                    {t('minimum_balance')}
                </span>
            </div>
        );
    };

    const renderAvailableBalance = () => {
        return (
            <div className={classes.balance}>
                <div>
                    {Intl.NumberFormat('en-IN', {
                        maximumFractionDigits: precisionValue,
                    }).format(Number(customerWalletData?.availableBalance || 0))}
                </div>
                <span className={classes.balanceText}>
                    {t('available_balance')}
                </span>
            </div>
        );
    };

    const renderBlockedBalance = () => {
        return (
            <div className={classes.balance}>
                <div>
                    {Intl.NumberFormat('en-IN', {
                        maximumFractionDigits: precisionValue,
                    }).format(Number(customerWalletData?.blockedBalance || 0))}
                </div>
                <span className={classes.balanceText}>
                    {t('blocked_amount')}
                </span>
            </div>
        );
    };

    const renderBalances = () => {
        return (
            <div className={classes.balanceDiv}>
                {renderIcon()}
                {renderCurrentBalance()}
                {renderSign('=')}
                {renderBlockedBalance()}
                {renderSign('+')}
                {renderAvailableBalance()}
                {renderSign('|')}
                {renderMinumumBalance()}
            </div>
        );
    };

    const renderRecharge = () => {
        return (
            <Button
                onClick={() => setRechargeVisible(true)}
                type="primary"
                style={{
                    height: 32,
                    fontSize: 12,
                    backgroundColor: uiTheme.primaryColor,
                    border: `1px solid ${uiTheme.primaryColor}`,
                    color: '#FFFFFF',
                    fontFamily: 'Open Sans',
                    fontWeight: 600,
                    letterSpacing: 0,
                    padding: '0px 24px',
                    marginLeft: 12,
                    borderRadius: 4,
                }}
            >
                {t('recharge')}
            </Button>
        );
    };

    const renderFAQsButton = () => {
        return (
            <Button
                onClick={() => setFaqVisible(true)}
                type="primary"
                style={{
                    height: 32,
                    fontSize: 12,
                    backgroundColor: uiTheme.primaryColor,
                    border: `1px solid ${uiTheme.primaryColor}`,
                    color: '#FFFFFF',
                    fontFamily: 'Open Sans',
                    fontWeight: 600,
                    letterSpacing: 0,
                    padding: '0px 24px',
                    borderRadius: 4,
                    marginLeft: 5,
                }}
            >
                {t('virtual_wallet_frequently_asked_questions_button_text')}
            </Button>
        );
    };

    const renderFAQsText = () => {
        return (
            <span className={classes.faqText}>
                {t('virtual_wallet_frequently_asked_questions_text')}
            </span>
        );
    };

    const renderFAQs = () => {
        return (
            <div className={classes.faqBox}>
                {renderFAQsText()}
                {renderFAQsButton()}
            </div>
        );
    };

    const renderBalance = () => {
        return (
            <div className={classes.cashNCarry}>
                {renderBalances()}
                <div className={classes.recharge}>
                    {renderFAQs()}
                    {renderRecharge()}
                </div>
            </div>
        );
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        if (!Number.isNaN(Number(inputValue))) {
            setAmount(Number(inputValue));
        } else {
            message.error(t('invalid_numeric_value_error'));
        }
    };

    const handleButtonClick = (value: number) => {
        setAmount((prevAmount) => prevAmount + value);
    };

    const closeRechargeModal = () => {
        setRechargeVisible(false);
        setAmount(0);
    };

    const renderContent = () => {
        if (loading) {
            return null;
        }

        return (
            <div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {renderDrawerIcon()}
                    <div style={{ marginLeft: '16px' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                            {Number(customerWalletData.availableBalance || 0)}
                        </div>
                        <div style={{ fontSize: '13px' }}>{t('available_balance')}</div>
                    </div>
                </div>
                <div style={{ marginTop: '30px' }}>{t('enter_amount')}</div>
                <Input style={{ width: '42%' }} value={amount} onChange={handleInputChange} />
                <div className={classes.paymentButtonsCollection}>
                    <Button type="primary" onClick={() => handleButtonClick(500)}>+ 500</Button>
                    <Button type="primary" onClick={() => handleButtonClick(1000)}>+ 1000</Button>
                    <Button type="primary" onClick={() => handleButtonClick(2000)}>+ 2000</Button>
                    <Button type="primary" onClick={() => handleButtonClick(5000)}>+ 5000</Button>
                    <Button type="primary" onClick={() => handleButtonClick(10000)}>+ 10000</Button>
                </div>
            </div>
        );
    };

    const redirectToPayphi = async (rechargeAmount: number, referenceId: string) => {
        setLoading(true);
        const response = await getRedirectionURL({ amount: rechargeAmount, referenceId });
        if (response?.isSuccess) {
            localStorage.setItem(AUTO_CHECK_FIRST_RECHARGE_ROW_KEY, '1');
            // can get transaction id from response as well
            const { data } = response;
            const url = data?.qrData?.data?.redirectURI;
            window.open(url, '_blank');
        } else {
            message.error(t('virtual_wallet_recharge_failed_error'));
        }
        setLoading(false);
    };

    const renderHeader = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: '650' }}>
                    {t('recharge_virtual_wallet')}
                </div>
                <Button type="primary" onClick={() => redirectToPayphi(amount, reference_id)}>
                    {t('recharge')}
                </Button>
            </div>
        );
    };

    const renderRechargeModal = () => {
        if (!rechargeVisible) {
            return null;
        }
        return (
            <Drawer
                title={renderHeader()}
                visible={rechargeVisible}
                onClose={() => closeRechargeModal()}
                footer={t('virtual_wallet_recharge_drawer_footer')}
                width="30%"
            >
                {loading && (
                    <Spin
                        style={{
                            marginLeft: '45%',
                            marginTop: 100,
                            marginBottom: 100,
                        }}
                    />
                )}
                {renderContent()}
            </Drawer>
        );
    };

    const renderFAQModal = () => {
        if (!faqVisible) {
            return null;
        }
        return (
            <WalletFaq
                onCancel={() => setFaqVisible(false)}
            />
        );
    };

    const renderFooter = () => {
        return (
            <div className={classes.footer}>
                {renderPaginator()}
            </div>
        );
    };

    return (
        <div className={classes.main}>
            {renderBalance()}
            {renderFilters()}
            <>
                {renderTable()}
                {renderFooter()}
            </>
            {renderRechargeModal()}
            {renderFAQModal()}
        </div>
    );
};
const mapStateToProps = (state: ReduxStore) => {
    return {
        uiTheme: state.uiTheme,
    };
};

const mapDispatchToProps = (dispatch: any) => (bindActionCreators({
    setCNCData: storeCNCDataState,
}, dispatch));

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
        mapDispatchToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: walletStyles,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default GenericHoc(hocConfig)(WalletPage);
